import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo, ReactNode } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, collection, getDocs, query, where } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig'; // Importiere die initialisierte Auth-Instanz und Firestore DB
import { Seller } from '../types/invoiceTypes'; // Importiere den Seller-Typ
import { AvailableTenant, PendingInvite, TenantRole } from '../types/tenantTypes';
import {
  TENANT_SCOPING_ENABLED,
  readStoredActiveTenantId,
  persistActiveTenantId,
  clearStoredActiveTenantId,
  setEffectiveScopeUid,
  setTenantIdHeaderValue,
} from '../utils/tenantScope';
import logger from '../utils/logger';
import { getClientInviteToken } from '../utils/clientInviteToken';
import { getPendingAccountType, clearPendingAccountType, PENDING_ACCOUNT_TYPE_KANZLEI } from '../utils/pendingAccountType';
import { logAdvisorContextEnter } from '../api/advisorAccessApi';
import i18n from 'i18next';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  sellerData: Seller | null;
  isPremium: boolean;
  sellerDataLoading: boolean;
  dataVersion: number;
  saveSellerData: (data: Partial<Seller>) => Promise<void>;
  refreshUserData: () => Promise<void>;
  // ── M11 W3: Tenant-/Mandanten-Scope (INERT hinter REACT_APP_TENANT_SCOPING_ENABLED) ──
  /** Aktive Tenant-Id der Session. Default = eigene uid (eigener impliziter Tenant). */
  activeTenantId: string;
  /** Auswählbare Tenants: eigener impliziter Tenant + jeder fremde mit aktivem Membership. */
  availableTenants: AvailableTenant[];
  /** Rolle des Users im aktiven Tenant ('owner' bei eigenem Tenant / Flag off). */
  activeRole: TenantRole;
  /**
   * Die Scope-uid, mit der Daten-Queries gescopt werden. Bei Flag off ODER eigenem
   * aktiven Tenant === `currentUser.uid` (Inertheits-Garantie). Weicht nur bei
   * aktivem fremden Tenant ab (== dessen scopeUid).
   */
  effectiveScopeUid: string;
  /** Wechselt den aktiven Tenant (no-op bei Flag off oder unbekannter Id). */
  switchTenant: (tenantId: string) => void;
  // ── M11b-1: Annahme-Flow für offene Einladungen ──
  /**
   * Offene (noch nicht angenommene) Einladungen des eingeloggten Users
   * (`tenant_members` mit `memberUid==uid && status=='invited'`). Getrennt von
   * `availableTenants` — ein offener Invite ist erst nach Annahme switch-bar.
   * Bei Flag off immer leer.
   */
  pendingInvites: PendingInvite[];
  /**
   * Nimmt eine Einladung an (invited→active) und macht den Tenant switch-bar.
   * Wirft bei Fehler (fail-closed, kein stiller Fallback).
   */
  acceptInvite: (membershipId: string) => Promise<void>;
  /**
   * Lädt `availableTenants` + `pendingInvites` neu (no-op ohne `currentUser`).
   * M11b Portal-Fix (Bug 3): vom Token-Accept-Panel nach erfolgreicher Annahme
   * aufgerufen, damit der neu aktivierte Mandanten-Tenant sofort switch-bar ist.
   */
  reloadTenants: () => Promise<void>;
  /**
   * Lehnt eine Einladung ab (invited→declined, terminal).
   * Wirft bei Fehler (fail-closed, kein stiller Fallback).
   */
  declineInvite: (membershipId: string) => Promise<void>;
  // ── WL-K5a: Reverse-Invite (Kanzlei → Mandant) — B-E5 Separations-Slot ──
  /**
   * Offene CLIENT-Invite-Token (Kanzlei→Mandant), aus dem eigenen localStorage-Key
   * `pendingClientInviteToken` abgeleitet. BEWUSST ein EIGENER Slot, NICHT
   * `pendingInvites`: `useIsAdvisorContext` liest `pendingInvites` (Signal c) — ein
   * frischer Mandant mit offenem Client-Invite darf NICHT als Advisor
   * fehlklassifiziert werden (sonst wird sein Onboarding unterdrückt, B-E5). Dieser
   * Slot wird von `useIsAdvisorContext` NIE gelesen. In v1 höchstens ein Eintrag
   * (der aktuell mitgeführte Token).
   */
  pendingClientInvites: string[];
  /**
   * Liest den Client-Invite-Token-State aus localStorage neu (nach Accept/Decline
   * aufzurufen, sobald der Token via `clearClientInviteToken()` entfernt wurde).
   */
  refreshPendingClientInvites: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

const removeUndefinedDeep = <T,>(value: T): T => {
  if (Array.isArray(value)) {
    return value
      .map(item => removeUndefinedDeep(item))
      .filter(item => item !== undefined) as T;
  }

  if (value instanceof Date || value === null || value === undefined) {
    return value;
  }

  if (typeof value === 'object') {
    const sanitizedObject = Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>(
      (acc, [key, nestedValue]) => {
        const sanitizedValue = removeUndefinedDeep(nestedValue);
        if (sanitizedValue !== undefined) {
          acc[key] = sanitizedValue;
        }
        return acc;
      },
      {}
    );
    return sanitizedObject as T;
  }

  return value;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sellerData, setSellerData] = useState<Seller | null>(null);
  const [isPremium, setIsPremium] = useState<boolean>(false);
  const [sellerDataLoading, setSellerDataLoading] = useState(true);
  const [dataVersion, setDataVersion] = useState(0);

  // ── M11 W3: Tenant-/Mandanten-Scope-State (INERT bei Flag off) ──
  const [activeTenantId, setActiveTenantId] = useState<string>('');
  const [availableTenants, setAvailableTenants] = useState<AvailableTenant[]>([]);
  // ── M11b Portal-Fix (Bug 3): synchrones Spiegel-Ref von availableTenants ──
  // switchTenant validiert das Ziel gegen dieses Ref (NICHT gegen den State), damit
  // ein Aufruf unmittelbar nach `await reloadTenants()` die FRISCHE Liste sieht (State-
  // Update ist erst nach Re-Render sichtbar → sonst stale-closure-No-op). `applyAvailable
  // Tenants` schreibt Ref + State IMMER synchron gemeinsam.
  const availableTenantsRef = useRef<AvailableTenant[]>([]);
  const applyAvailableTenants = useCallback((tenants: AvailableTenant[]) => {
    availableTenantsRef.current = tenants;
    setAvailableTenants(tenants);
  }, []);
  // ── M-OB-10 (§7.5, 🟢): synchrones Spiegel-Ref der aktiven Tenant-Id ──
  // Erlaubt `loadAvailableTenants`, einen inzwischen entfernten aktiven Fremd-
  // Tenant (z.B. nach Widerruf) gegen die FRISCH geladene Liste zu erkennen und
  // fail-safe auf den eigenen Tenant zurückzufallen — ohne `activeTenantId` in die
  // Loader-Deps zu ziehen. Wird per Effekt synchron gehalten.
  const activeTenantIdRef = useRef<string>('');
  // ── M11b-1: offene Einladungen (getrennt von availableTenants) ──
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  // ── WL-K5a: eigener Client-Invite-Slot (B-E5, getrennt von pendingInvites) ──
  const [pendingClientInvites, setPendingClientInvites] = useState<string[]>([]);
  const refreshPendingClientInvites = useCallback(() => {
    const token = getClientInviteToken();
    setPendingClientInvites(token ? [token] : []);
  }, []);
  // Populate the client-invite slot whenever auth state settles (the token was
  // written by RegisterPage/LoginPage before the redirect chain). Recomputed on
  // login/logout; the accept/decline panel calls refreshPendingClientInvites()
  // after clearing the token.
  useEffect(() => {
    refreshPendingClientInvites();
  }, [currentUser, refreshPendingClientInvites]);

  const loadUserData = useCallback(async (userId: string) => {
    if (!userId) return;
    
    setSellerDataLoading(true);
    const sellerDocRef = doc(db, 'sellers', userId);
    
    try {
      const docSnap = await getDoc(sellerDocRef);
      if (docSnap.exists()) {
        const data = docSnap.data() as Seller;
        setSellerData(data);
        setIsPremium(data.isPremium === true);
        
        // Sprachlogik: 
        // 1. Wenn User preferredLanguage in Firebase hat -> verwenden
        // 2. Wenn User kein preferredLanguage hat -> aktuelle Sprache (oder 'de') in Firebase speichern
        if (data.preferredLanguage) {
          // Sprache aus Firebase laden
          if (data.preferredLanguage !== i18n.language) {
            console.log('Applying user language preference from Firebase:', data.preferredLanguage);
            await i18n.changeLanguage(data.preferredLanguage);
            localStorage.setItem('i18nextLng', data.preferredLanguage);
            document.documentElement.lang = data.preferredLanguage;
          }
        } else {
          // Bestehender User ohne preferredLanguage -> aktuelle Sprache (oder 'de') setzen
          const currentLang = i18n.language || 'de';
          console.log('User has no language preference, saving current language:', currentLang);
          try {
            await setDoc(sellerDocRef, { preferredLanguage: currentLang }, { merge: true });
            // Lokalen State aktualisieren
            setSellerData(prev => prev ? { ...prev, preferredLanguage: currentLang } : prev);
          } catch (saveError) {
            console.debug('Could not save initial language preference:', saveError);
          }
        }

      } else {
        console.log("No seller document found for user:", userId);
        setSellerData(null);
        setIsPremium(false);
      }
    } catch (error) {
      console.error("Error fetching seller data:", error);
      setSellerData(null);
      setIsPremium(false);
    } finally {
      setSellerDataLoading(false);
      setDataVersion(v => v + 1);
    }
  }, []);

  const refreshUserData = useCallback(async () => {
    if (currentUser) {

      await loadUserData(currentUser.uid);
      return;
    }
    console.log("Cannot refresh, no user logged in");
  }, [currentUser, loadUserData]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setLoading(false);

      if (user) {
        await loadUserData(user.uid);
        
        // Speichere die Auth-Email im Seller-Dokument (für Admin-Dashboard)
        if (user.email) {
          const sellerDocRef = doc(db, 'sellers', user.uid);
          try {
            await setDoc(sellerDocRef, { 
              authEmail: user.email,
              lastLoginAt: new Date()
            }, { merge: true });
          } catch (error) {
            // Ignoriere Fehler beim Speichern der Auth-Email (nicht kritisch)
            console.debug("Could not save auth email:", error);
          }
        }
      } else {
        setSellerData(null);
        setIsPremium(false);
        setSellerDataLoading(false);
      }
    });

    return unsubscribe;
  }, [loadUserData]);

  const saveSellerData = async (data: Partial<Seller>) => {
    if (!currentUser) {
      throw new Error("User not logged in, cannot save seller data.");
    }
    
    // Geschützte Felder, die ausschließlich vom Backend atomar verwaltet werden.
    // Diese dürfen NICHT vom Frontend überschrieben werden, da sonst
    // Rechnungs-/Angebotsnummern auf veraltete gecachte Werte zurückgesetzt werden.
    const PROTECTED_BACKEND_FIELDS = [
      'invoiceNumberCurrent',
      'offerNumberCurrent',
      'monthlyInvoiceCount',
      // loopBBeta (WL-K0c) ist backend-only wie isPremium: nur die serverseitigen
      // Contagion-Writer/das Backfill dürfen es setzen. Ein Client-Write würde
      // sonst den Server-Gate (isLoopBBetaServer liest exakt dieses Feld)
      // aushebeln → hier strippen (Rules verbieten es zusätzlich hart).
      'loopBBeta',
      // PR-T2 (Pricing-Repackaging §3.6): `plan`/`grandfatheredFeatures` sind
      // ausschließlich vom Stripe-Webhook bzw. vom Admin-Backfill zu setzen
      // (Rules verbieten Client-Writes zusätzlich hart, siehe firestore.rules).
      'plan',
      'grandfatheredFeatures',
      // M-G2 (Grace-Window §5.4): `grandfatheredGraceUntil` ist die befristete
      // Entsprechung zu `grandfatheredFeatures` — ausschließlich vom
      // `backfillGraceWindow`-Admin-Backfill zu setzen (Rules verbieten
      // Client-Writes zusätzlich hart, siehe firestore.rules).
      'grandfatheredGraceUntil',
      // Robustheits-Nachzug (D-A1): `isPremium`/`apiAccess` waren bisher NICHT
      // gestrippt, obwohl die Rules sie schon immer hart verbieten — ein
      // versehentlich mitgereichtes `isPremium` ließ dadurch den GESAMTEN Write
      // an den Rules scheitern statt nur das Feld zu ignorieren. Beide Felder
      // dürfen ohnehin nie vom Client geschrieben werden (Webhook/graceEnforcer
      // sind die einzigen legitimen Writer).
      'isPremium',
      'apiAccess',
    ];
    
    const sanitizedData = removeUndefinedDeep({ ...data });
    for (const field of PROTECTED_BACKEND_FIELDS) {
      if (field in sanitizedData) {
        console.warn(`[saveSellerData] Geschütztes Feld '${field}' wird nicht gespeichert (nur Backend darf dieses Feld ändern).`);
        delete (sanitizedData as Record<string, unknown>)[field];
      }
    }
    
    const sellerDocRef = doc(db, 'sellers', currentUser.uid);
    try {
      await setDoc(sellerDocRef, sanitizedData, { merge: true });
      
      setSellerData(prevData => {
        const updatedData = { ...prevData, ...sanitizedData } as Seller;
        console.log("Local sellerData updated optimistically:", updatedData);
        return updatedData;
      });
      setDataVersion(v => v + 1);
      
      if (data.isPremium !== undefined) {
        setIsPremium(data.isPremium === true);
      }
      
      console.log("Seller data saved successfully to Firestore and local state updated.");

    } catch (error) {
      console.error("Error saving seller data:", error);
      throw error;
    }
  };

  // ── M-OB-11 (§8.1): Kanzlei-Kennzeichnung aus der Registrierung nachziehen ──
  // Der Registrierungs-Marker (`pendingAccountType`) wird von RegisterPage bei
  // erfolgreicher Registrierung gesetzt und überlebt die Auth-Redirect-Kette.
  // Sobald das sellerData eines EINGELOGGTEN Nutzers geladen ist, wird
  // `accountType='kanzlei'` einmalig persistiert. `accountType` ist NICHT
  // backend-protected (reines UX-Signal) — `saveSellerData` schreibt es durch.
  //
  // KRITISCHE Footguns (§8.1):
  //  - Marker NIEMALS im auth-null-Zustand anfassen: der `!currentUser`-Guard
  //    unten sorgt dafür, dass beim Boot (vor Login) der Marker unberührt bleibt
  //    (sonst würde er verschluckt, bevor der frisch registrierte Nutzer
  //    eingeloggt ist).
  //  - Idempotent: nach dem Write ist `sellerData.accountType==='kanzlei'` ⇒
  //    Re-Run räumt nur den Marker auf; der In-Flight-Ref verhindert parallele
  //    Doppel-Writes während des laufenden `await`.
  //  - Kein `?? undefined` in den Write: es wird ausschließlich das Literal
  //    `{ accountType: 'kanzlei' }` geschrieben.
  const accountTypePersistInFlight = useRef(false);
  useEffect(() => {
    // Nur nach abgeschlossenem Load eines eingeloggten Nutzers.
    if (!currentUser || sellerDataLoading) return;
    if (accountTypePersistInFlight.current) return;

    if (getPendingAccountType() !== PENDING_ACCOUNT_TYPE_KANZLEI) return;

    if (sellerData?.accountType === PENDING_ACCOUNT_TYPE_KANZLEI) {
      // Bereits gesetzt ⇒ nur den Marker aufräumen (idempotent).
      clearPendingAccountType();
      return;
    }

    accountTypePersistInFlight.current = true;
    void (async () => {
      try {
        await saveSellerData({ accountType: 'kanzlei' });
        clearPendingAccountType();
      } catch (err) {
        // Marker bewusst NICHT löschen ⇒ Retry beim nächsten Load. Kein
        // Blocker für die App (rein kosmetisches UX-Signal).
        console.error('[AuthContext] Kanzlei-accountType konnte nicht persistiert werden:', err);
      } finally {
        accountTypePersistInFlight.current = false;
      }
    })();
    // saveSellerData ist bewusst nicht in den Deps (bei jedem Render neu
    // erzeugt, würde den Effekt sonst pro Render feuern) — es schließt über
    // `currentUser`, das in den Deps steht.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, sellerDataLoading, sellerData]);

  // ── M11 W3: Tenant-Scope-Auflösung (INERT bei Flag off) ────────────────────
  // Lädt die verfügbaren Tenants. Bei Flag off (oder ohne Membership) bleibt es
  // beim eigenen impliziten Tenant — KEINE zusätzliche Firestore-Query, kein
  // Verhaltensdelta zum Status quo.
  const loadAvailableTenants = useCallback(
    async (uid: string, ownDisplayName: string) => {
      const ownTenant: AvailableTenant = {
        tenantId: uid,
        scopeUid: uid,
        role: 'owner',
        displayName: ownDisplayName,
      };

      if (!TENANT_SCOPING_ENABLED) {
        // Inert: nur eigener Tenant, keine Query.
        applyAvailableTenants([ownTenant]);
        return;
      }

      try {
        const membersRef = collection(db, 'tenant_members');
        const membersQuery = query(
          membersRef,
          where('memberUid', '==', uid),
          where('status', '==', 'active'),
        );
        const snap = await getDocs(membersQuery);

        const tenants: AvailableTenant[] = [ownTenant];
        for (const memberDoc of snap.docs) {
          const data = memberDoc.data();
          const tenantId = typeof data.tenantId === 'string' ? data.tenantId : null;
          // Eigenen Tenant nicht doppeln; ungültige Datensätze überspringen.
          if (!tenantId || tenantId === uid) {
            continue;
          }

          let scopeUid = typeof data.scopeUid === 'string' ? data.scopeUid : tenantId;
          let displayName = tenantId;
          const role: TenantRole =
            data.role === 'owner' || data.role === 'member' || data.role === 'advisor'
              ? data.role
              : 'advisor';

          try {
            const tenantDoc = await getDoc(doc(db, 'tenants', tenantId));
            if (tenantDoc.exists()) {
              const td = tenantDoc.data();
              if (typeof td.displayName === 'string' && td.displayName) {
                displayName = td.displayName;
              }
              if (typeof td.scopeUid === 'string' && td.scopeUid) {
                scopeUid = td.scopeUid;
              }
            }
          } catch (tenantErr) {
            // Tenant-Doc nicht lesbar → denormalisierte Werte aus dem Membership nutzen.
            logger.warn('[AuthContext] Could not load tenant doc, using membership fields', tenantErr);
          }

          tenants.push({ tenantId, scopeUid, role, displayName });
        }

        applyAvailableTenants(tenants);

        // M-OB-10 (§7.5, 🟢): Ist der aktuell aktive Fremd-Tenant in der FRISCH
        // geladenen (autoritativen) Liste nicht mehr enthalten — typischerweise
        // nach einem Widerruf durch den Mandanten (status→'revoked' ⇒ fällt aus
        // der `status=='active'`-Query) — fail-safe auf den eigenen Tenant
        // zurückfallen. Verhindert, dass Consumer weiter mit dem fremden Scope
        // queryen und in eine permission-denied-Schleife laufen. Sicher gegen den
        // Boot-Restore: geprüft wird ausschließlich gegen die vollständige, gerade
        // geladene Liste (nicht gegen einen Zwischenzustand).
        const active = activeTenantIdRef.current;
        if (active && active !== uid && !tenants.some((tt) => tt.tenantId === active)) {
          logger.warn('[AuthContext] active tenant no longer available, resetting to own tenant', active);
          setActiveTenantId(uid);
          clearStoredActiveTenantId();
        }
      } catch (err) {
        // Fail-safe: bei jedem Fehler ausschließlich der eigene Tenant — KEIN fremder Zugriff.
        logger.error('[AuthContext] Failed to load available tenants, falling back to own tenant', err);
        applyAvailableTenants([ownTenant]);
      }
    },
    [applyAvailableTenants],
  );

  // ── M11b-1: offene Einladungen laden (getrennt von availableTenants) ────────
  // Query `memberUid==uid && status=='invited'` (Rules erlauben dies via
  // `list if memberUid==auth.uid`). Bewusst NICHT in availableTenants gemischt:
  // ein nicht-angenommener Invite darf nie switch-bar sein. Fail-safe: bei
  // jedem Fehler leeres Array — kein fremder Zugriff, kein Crash.
  const loadPendingInvites = useCallback(async (uid: string) => {
    if (!TENANT_SCOPING_ENABLED) {
      // Inert: bei Flag off existieren keine fremden Memberships → keine Query.
      setPendingInvites([]);
      return;
    }

    try {
      const membersRef = collection(db, 'tenant_members');
      const pendingQuery = query(
        membersRef,
        where('memberUid', '==', uid),
        where('status', '==', 'invited'),
      );
      const snap = await getDocs(pendingQuery);

      const invites: PendingInvite[] = [];
      for (const memberDoc of snap.docs) {
        const data = memberDoc.data();
        const tenantId = typeof data.tenantId === 'string' ? data.tenantId : null;
        // Eigene Einladung in den eigenen Tenant ist sinnlos; ungültige Datensätze überspringen.
        if (!tenantId || tenantId === uid) {
          continue;
        }
        const scopeUid = typeof data.scopeUid === 'string' ? data.scopeUid : tenantId;
        const role: TenantRole =
          data.role === 'owner' || data.role === 'member' || data.role === 'advisor'
            ? data.role
            : 'advisor';

        let displayName = tenantId;
        try {
          const tenantDoc = await getDoc(doc(db, 'tenants', tenantId));
          if (tenantDoc.exists()) {
            const td = tenantDoc.data();
            if (typeof td.displayName === 'string' && td.displayName) {
              displayName = td.displayName;
            }
          }
        } catch (tenantErr) {
          // Tenant-Doc nicht lesbar → denormalisierten Fallback (tenantId) nutzen.
          logger.warn('[AuthContext] Could not load tenant doc for pending invite', tenantErr);
        }

        invites.push({ membershipId: memberDoc.id, tenantId, scopeUid, role, displayName });
      }

      setPendingInvites(invites);
    } catch (err) {
      logger.error('[AuthContext] Failed to load pending invites', err);
      setPendingInvites([]);
    }
  }, []);

  // Einladung annehmen: invited→active. Die Firestore-Rules erlauben dem Mitglied
  // exakt diesen Übergang bei eingefrorenem role/scopeUid/memberUid; updateDoc mit
  // nur {status, acceptedAt} lässt die übrigen Felder unverändert. Danach beide
  // Listen neu laden: der Invite verschwindet aus pendingInvites, der nun aktive
  // Tenant erscheint in availableTenants (= switch-bar).
  const acceptInvite = useCallback(
    async (membershipId: string) => {
      if (!currentUser) {
        throw new Error('User not logged in, cannot accept invite.');
      }
      const memberRef = doc(db, 'tenant_members', membershipId);
      try {
        await updateDoc(memberRef, { status: 'active', acceptedAt: serverTimestamp() });
      } catch (err) {
        logger.error('[AuthContext] Failed to accept invite', err);
        throw err;
      }

      const ownDisplayName = sellerData?.name || currentUser.email || currentUser.uid;
      await loadPendingInvites(currentUser.uid);
      await loadAvailableTenants(currentUser.uid, ownDisplayName);
    },
    [currentUser, sellerData, loadPendingInvites, loadAvailableTenants],
  );

  // Einladung ablehnen: invited→declined (terminal). Additiver Rules-Zweig (§4.1)
  // erlaubt dem Mitglied genau diesen Übergang bei eingefrorenen Identitätsfeldern.
  const declineInvite = useCallback(
    async (membershipId: string) => {
      if (!currentUser) {
        throw new Error('User not logged in, cannot decline invite.');
      }
      const memberRef = doc(db, 'tenant_members', membershipId);
      try {
        await updateDoc(memberRef, { status: 'declined', declinedAt: serverTimestamp() });
      } catch (err) {
        logger.error('[AuthContext] Failed to decline invite', err);
        throw err;
      }

      await loadPendingInvites(currentUser.uid);
    },
    [currentUser, loadPendingInvites],
  );

  // M11b Portal-Fix (Bug 3): beide Tenant-Listen neu laden. Vom Token-Accept-Panel nach
  // erfolgreicher Annahme awaited aufgerufen — danach ist der neue Mandant sowohl in
  // availableTenants (switch-bar) als auch der konsumierte Invite aus pendingInvites raus.
  // no-op ohne currentUser. ownDisplayName identisch zum Load-Effect.
  const reloadTenants = useCallback(async () => {
    if (!currentUser) {
      return;
    }
    const ownDisplayName = sellerData?.name || currentUser.email || currentUser.uid;
    await loadAvailableTenants(currentUser.uid, ownDisplayName);
    await loadPendingInvites(currentUser.uid);
  }, [currentUser, sellerData, loadAvailableTenants, loadPendingInvites]);

  // Tenants + offene Einladungen laden, sobald User + (idealerweise) sellerData verfügbar sind.
  useEffect(() => {
    if (!currentUser) {
      applyAvailableTenants([]);
      setPendingInvites([]);
      return;
    }
    const ownDisplayName =
      sellerData?.name || currentUser.email || currentUser.uid;
    loadAvailableTenants(currentUser.uid, ownDisplayName);
    loadPendingInvites(currentUser.uid);
  }, [currentUser, sellerData, loadAvailableTenants, loadPendingInvites, applyAvailableTenants]);

  // Aktiven Tenant bestimmen: Default eigene uid; bei Flag on aus localStorage wiederherstellen.
  useEffect(() => {
    if (!currentUser) {
      setActiveTenantId('');
      return;
    }
    if (!TENANT_SCOPING_ENABLED) {
      setActiveTenantId(currentUser.uid);
      return;
    }
    const stored = readStoredActiveTenantId();
    setActiveTenantId(stored || currentUser.uid);
  }, [currentUser]);

  // activeTenantId synchron ins Ref spiegeln (für loadAvailableTenants' Stale-Check).
  useEffect(() => {
    activeTenantIdRef.current = activeTenantId;
  }, [activeTenantId]);

  // Der aktive Tenant (oder undefined, wenn die gespeicherte Id keinem Membership entspricht).
  const activeTenant = useMemo(
    () => availableTenants.find((tenant) => tenant.tenantId === activeTenantId),
    [availableTenants, activeTenantId],
  );

  // effectiveScopeUid: Inertheits-Garantie — Flag off ODER eigener Tenant ⇒ currentUser.uid.
  const effectiveScopeUid = useMemo(() => {
    if (!currentUser) {
      return '';
    }
    if (!TENANT_SCOPING_ENABLED) {
      return currentUser.uid;
    }
    // Fail-safe: unbekannter/abgelaufener aktiver Tenant ⇒ eigene uid (nie fremder Scope).
    return activeTenant?.scopeUid ?? currentUser.uid;
  }, [currentUser, activeTenant]);

  const activeRole: TenantRole = activeTenant?.role ?? 'owner';

  // ── M-OB-10 (§7.3): Session-Grain-Audit beim Eintritt in einen Advisor-Kontext ──
  // Sobald der aufgelöste aktive Tenant eine Advisor-Kante des Nutzers ist, wird
  // EIN `context_enter`-Audit-Event geschrieben. Ein EINZIGER Ort deckt BEIDE
  // Auslöser ab: `switchTenant` (setzt `activeTenantId` → `activeTenant`-Memo) UND
  // der Boot-Restore aus localStorage (der `activeTenant` löst auf, sobald
  // `availableTenants` geladen ist). Fire-and-forget: Fehler werden geschluckt und
  // dürfen den Kontextwechsel NIE blockieren (der Server ist auth-only + pass-
  // through bei Flag off ⇒ inert, nur ein additiver Audit-Write).
  //
  // Idempotenz/Sicherheit:
  //  - `lastLoggedAdvisorTenantRef` verhindert Doppel-Feuerung bei Re-Renders und
  //    bei neuen Array-Referenzen aus `loadAvailableTenants` (Vergleich per
  //    tenantId-String, nicht per Objekt-Identität).
  //  - auth-null ⇒ NICHT feuern (Ref zurücksetzen). Analog Wave-D-Marker-Lehre.
  //  - Verlassen des Advisor-Kontexts (eigener/Owner-Tenant) setzt den Ref zurück,
  //    damit ein späterer Wiedereintritt erneut protokolliert wird (Session-Grain).
  const lastLoggedAdvisorTenantRef = useRef<string | null>(null);
  useEffect(() => {
    if (!currentUser) {
      lastLoggedAdvisorTenantRef.current = null;
      return;
    }
    // Nur ein echter fremder Advisor-Tenant löst Audit aus; alles andere setzt den
    // Marker zurück (kein Feuern beim eigenen/Owner-Tenant).
    if (
      !activeTenant ||
      activeTenant.role !== 'advisor' ||
      activeTenant.tenantId === currentUser.uid
    ) {
      lastLoggedAdvisorTenantRef.current = null;
      return;
    }
    if (lastLoggedAdvisorTenantRef.current === activeTenant.tenantId) {
      return; // Für diesen Kontext bereits protokolliert.
    }
    lastLoggedAdvisorTenantRef.current = activeTenant.tenantId;
    // `Promise.resolve(...)` toleriert auch einen (in Tests ge-reseteten) Mock, der
    // synchron `undefined` liefert — kein `.catch`-of-undefined-Throw im Effekt.
    void Promise.resolve(logAdvisorContextEnter(activeTenant.scopeUid)).catch((err) => {
      logger.warn('[AuthContext] logAdvisorContextEnter failed (non-blocking)', err);
    });
  }, [currentUser, activeTenant]);

  // X-Tenant-Id-Header-Wert: NUR ein gegen `availableTenants` validierter FREMDER
  // Tenant erzeugt einen Header; eigener/unbekannter/abgelaufener Tenant ⇒ null
  // (kein Header → PHP-Provider fällt fail-safe auf eigenen Tenant). Aus demselben
  // validierten `activeTenant` abgeleitet wie effectiveScopeUid ⇒ Scope und Header
  // referenzieren IMMER denselben Tenant (W4 Carry-forward (f)).
  const tenantIdHeaderValue = useMemo(() => {
    if (!currentUser || !TENANT_SCOPING_ENABLED) {
      return null;
    }
    if (activeTenant && activeTenant.tenantId !== currentUser.uid) {
      return activeTenant.tenantId;
    }
    return null;
  }, [currentUser, activeTenant]);

  // effectiveScopeUid + validierten Tenant-Header für Nicht-Hook-Module
  // (Services, apiRequest) spiegeln.
  useEffect(() => {
    setEffectiveScopeUid(effectiveScopeUid || null);
    setTenantIdHeaderValue(tenantIdHeaderValue);
  }, [effectiveScopeUid, tenantIdHeaderValue]);

  // Beim Logout den persistierten Tenant räumen (kein Carry-over zwischen Accounts).
  useEffect(() => {
    if (!currentUser) {
      clearStoredActiveTenantId();
      setEffectiveScopeUid(null);
      setTenantIdHeaderValue(null);
    }
  }, [currentUser]);

  const switchTenant = useCallback(
    (tenantId: string) => {
      if (!TENANT_SCOPING_ENABLED || !currentUser) {
        // Inert: kein Wechsel möglich.
        return;
      }
      // M11b Portal-Fix (Bug 3): gegen das synchrone Ref validieren (NICHT gegen den
      // State), damit ein Aufruf direkt nach `await reloadTenants()` die frische Liste
      // sieht und nicht am stale-closure-State no-oppt.
      const target = availableTenantsRef.current.find((tenant) => tenant.tenantId === tenantId);
      if (!target) {
        logger.warn('[AuthContext] switchTenant ignored: unknown tenantId', tenantId);
        return;
      }
      setActiveTenantId(tenantId);
      persistActiveTenantId(tenantId);
      // Daten-Reload anstoßen: Consumer, die effectiveScopeUid in ihren Deps lesen,
      // re-fetchen ohnehin; dataVersion deckt zusätzlich darauf gekeyte Komponenten ab.
      setDataVersion((v) => v + 1);
    },
    [currentUser],
  );

  const value: AuthContextType = {
    currentUser,
    loading,
    sellerData,
    isPremium,
    sellerDataLoading,
    dataVersion,
    saveSellerData,
    refreshUserData,
    activeTenantId,
    availableTenants,
    activeRole,
    effectiveScopeUid,
    switchTenant,
    pendingInvites,
    acceptInvite,
    declineInvite,
    reloadTenants,
    pendingClientInvites,
    refreshPendingClientInvites,
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 